import { faToggleOff, faToggleOn } from '@fortawesome/pro-solid-svg-icons';
import { ApiService } from '@supply-mover-2-0/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { TemplatePreview } from '../../components/display/TemplatePreview';
import { Button } from '../../components/forms/Button';
import { PlainTextTemplateEditor } from '../../components/forms/PlainTextTemplateEditor';
import { SelectSearch } from '../../components/forms/SelectSearch';
import {
	VariableMapping,
	VariableMappingVariable,
} from '../../components/forms/VariableMapping';
import { Modal } from '../../components/overlays/Modal';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import { convertDataSourcesToVariables } from '../utils/dataSourceToVariables';
import { DynamicFieldValue } from './DynamicFieldValue';
import { PhoneNumberField } from './PhoneNumberField';

interface SmsStepConfigProps {
	config: any;
	setConfig: (config: any) => void;
	getAvailableDataSources: () => any[];
	getDisplayText: (value: any) => string;
	provisionedPhones: Array<{ phoneNumber: string }>;
	phonesLoading: boolean;
}

export function SmsStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
	provisionedPhones,
	phonesLoading,
}: SmsStepConfigProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();

	// Track intended mode - initialize from config.smsMode if it exists, otherwise infer from templateId
	const [intendedMode, setIntendedMode] = useState<'manual' | 'template'>(
		config.smsMode ?? (config.templateId != null ? 'template' : 'manual'),
	);
	const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
	const [templatePreview, setTemplatePreview] = useState<string>('');
	const [loadingPreview, setLoadingPreview] = useState(false);
	const [showVariableMapping, setShowVariableMapping] = useState(false);
	const [showBodyVariableMapping, setShowBodyVariableMapping] = useState(false);

	// Load template on mount if templateId exists
	useEffect(() => {
		if (config.templateId != null && selectedTemplate == null) {
			loadTemplateDetails(String(config.templateId));
		}
	}, [config.templateId]);

	// Update intended mode when config.smsMode changes (e.g., when modal reopens)
	useEffect(() => {
		if (config.smsMode != null) {
			setIntendedMode(config.smsMode);
		}
	}, [config.smsMode]);

	// Load recent templates
	const loadRecentTemplates = useCallback(async () => {
		try {
			const response = await ApiService.apiGet('/messaging/templates', {
				page: 1,
				limit: 10,
			});
			const templates = response.templates.map((template: any) => ({
				value: template.id.toString(),
				label: template.name,
				subtitle: template.description,
			}));

			// If we have a selected template that's not in recent, fetch and add it
			if (
				config.templateId != null &&
				!templates.find((t: any) => t.value === String(config.templateId))
			) {
				try {
					const selectedTemplateData = await ApiService.apiGet(
						`/messaging/templates/${config.templateId}`,
					);
					templates.unshift({
						value: selectedTemplateData.id.toString(),
						label: selectedTemplateData.name,
						subtitle: selectedTemplateData.description,
					});
				} catch (error) {
					console.debug(
						'Could not load selected template for recent list:',
						error,
					);
				}
			}

			return templates;
		} catch (error) {
			console.error('Failed to load recent templates:', error);

			return [];
		}
	}, [config.templateId]);

	// Search templates
	const searchTemplates = useCallback(async (query: string) => {
		try {
			const response = await ApiService.apiGet('/messaging/templates', {
				page: 1,
				limit: 20,
				search: query,
			});

			return response.templates.map((template: any) => ({
				value: template.id.toString(),
				label: template.name,
				subtitle: template.description,
			}));
		} catch (error) {
			console.error('Failed to search templates:', error);

			return [];
		}
	}, []);

	// Load template details and preview
	const loadTemplateDetails = async (templateId: string) => {
		try {
			setLoadingPreview(true);
			const template = await ApiService.apiGet(
				`/messaging/templates/${templateId}`,
			);
			setSelectedTemplate(template);
			setTemplatePreview(template.body ?? '');
		} catch (error) {
			console.error('Failed to load template details:', error);
			Toast.show({
				type: 'error',
				text1: t('Failed to load template'),
			});
		} finally {
			setLoadingPreview(false);
		}
	};

	// Handle template selection
	const handleTemplateSelect = (templateId: string) => {
		setConfig({
			...config,
			smsMode: 'template',
			templateId: templateId ? parseInt(templateId, 10) : undefined,
		});

		if (templateId) {
			loadTemplateDetails(templateId);
		} else {
			setSelectedTemplate(null);
			setTemplatePreview('');
		}
	};

	// Handle variable mapping save
	const handleSaveVariableMapping = (mapping: Record<string, string>) => {
		setConfig({
			...config,
			templateVariables: mapping,
		});
		setShowVariableMapping(false);
	};

	// Get template variables for mapping
	const getTemplateVariables = (): VariableMappingVariable[] => {
		if (selectedTemplate?.variables == null) {
			return [];
		}

		return selectedTemplate.variables.map((v: any) => ({
			name: v.name,
			type: v.type,
			required: v.required,
			description: v.description,
			defaultValue: v.defaultValue,
		}));
	};

	// Convert available data sources to context-aware variables for the merge variable picker
	const contextAwareVariables = useMemo(() => {
		const dataSources = getAvailableDataSources();

		return convertDataSourcesToVariables(dataSources);
	}, [getAvailableDataSources]);

	// Get variables from the mapping (not from body content)
	const getBodyVariablesForMapping =
		useCallback((): VariableMappingVariable[] => {
			const mapping = config.bodyVariableMapping ?? {};

			return Object.keys(mapping).map((varName) => ({
				name: varName,
				type: 'string',
				required: false,
				description: 'Custom variable for SMS body',
			}));
		}, [config.bodyVariableMapping]);

	// Handle saving body variable mapping
	const handleSaveBodyVariableMapping = useCallback(
		(mapping: Record<string, string>) => {
			setConfig({
				...config,
				bodyVariableMapping: mapping,
			});
			setShowBodyVariableMapping(false);
		},
		[config, setConfig],
	);

	// Calculate character count for SMS (standard SMS is 160 chars, or 70 for unicode)
	const characterCount = (config.body ?? '').length;
	const hasUnicode = /[^\x00-\x7F]/.test(config.body ?? '');
	const maxCharsPerSegment = hasUnicode ? 70 : 160;
	const segmentCount = Math.ceil(characterCount / maxCharsPerSegment) || 1;

	return (
		<div className="space-y-6">
			{/* To Field (phone number) */}
			<DynamicFieldValue
				label={t('To (Phone Number)')}
				value={config.toNumber ?? ''}
				onChange={(value) => setConfig({ ...config, toNumber: value })}
				dataType="phone"
				placeholder={t('Enter phone number or select from data')}
				required={true}
				description={t(
					'Phone number to send SMS to. Use dynamic values to send to different numbers based on execution data.',
				)}
				availableData={getAvailableDataSources()}
				displayTransform={getDisplayText}
			/>

			{/* From Phone Number */}
			<PhoneNumberField
				label={t('From Phone Number')}
				value={config.fromNumber ?? ''}
				onChange={(value) => setConfig({ ...config, fromNumber: value })}
				required={true}
				description={t(
					'Phone number to send SMS from. Select a provisioned number or use a dynamic value.',
				)}
				availableData={getAvailableDataSources()}
				provisionedPhones={provisionedPhones}
				phonesLoading={phonesLoading}
			/>

			{/* SMS Body Section */}
			<div>
				<div className="flex items-center justify-between mb-3">
					<label
						className={`text-sm font-medium ${colors.classes.textPrimary}`}
					>
						{t('Message Body')}
					</label>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							const newMode = intendedMode === 'manual' ? 'template' : 'manual';
							setIntendedMode(newMode);
							setConfig({
								...config,
								smsMode: newMode,
							});
						}}
						className="flex items-center gap-2"
						title={
							intendedMode === 'template'
								? t('Switch to Manual Input')
								: t('Switch to Use Template')
						}
					>
						<Icon
							icon={intendedMode === 'template' ? faToggleOn : faToggleOff}
							className={
								intendedMode === 'template'
									? colors.classes.statusSuccess
									: colors.classes.textSecondary
							}
							size={16}
						/>
						<span className="text-xs font-medium">
							{intendedMode === 'template'
								? t('Use Template')
								: t('Manual Input')}
						</span>
					</Button>
				</div>

				{/* Body Content based on mode */}
				<div className="mt-4">
					{intendedMode === 'template' ? (
						<>
							{/* Template Selection with SelectSearch */}
							<div className="mb-4">
								<SelectSearch
									mode="dynamic"
									value={config.templateId?.toString()}
									onChange={handleTemplateSelect}
									placeholder={t('Search SMS templates...')}
									loadRecent={loadRecentTemplates}
									searchItems={searchTemplates}
									recentGroupLabel={t('Recent Templates')}
									searchGroupLabel={t('Search Results')}
									emptyRecentMessage={t('No recent templates')}
									emptySearchMessage={t('No templates found')}
									searchDebounceMs={300}
									minSearchLength={1}
									fullWidth
								/>
							</div>

							{/* Template Preview */}
							{selectedTemplate != null && (
								<div>
									<div className="flex justify-between items-center mb-2">
										<label
											className={`text-sm font-medium ${colors.classes.textSecondary}`}
										>
											{t('Template Preview')}
										</label>
										{selectedTemplate.variables != null &&
											selectedTemplate.variables.length > 0 && (
												<Button
													size="xs"
													variant="outline"
													onClick={() => setShowVariableMapping(true)}
												>
													{t('Map Variables')} (
													{selectedTemplate.variables.length})
												</Button>
											)}
									</div>
									<TemplatePreview
										content={templatePreview}
										loading={loadingPreview}
										maxHeight="200px"
									/>
								</div>
							)}

							{/* Variable Mapping Modal */}
							{showVariableMapping && selectedTemplate != null && (
								<Modal
									isOpen={showVariableMapping}
									onClose={() => setShowVariableMapping(false)}
									title={t('Map Template Variables')}
								>
									<VariableMapping
										variables={getTemplateVariables()}
										mapping={config.templateVariables ?? {}}
										onSave={handleSaveVariableMapping}
										onCancel={() => setShowVariableMapping(false)}
										useDynamicFieldValue={true}
										availableData={getAvailableDataSources()}
										displayTransform={getDisplayText}
									/>
								</Modal>
							)}
						</>
					) : (
						<>
							{/* Manual Body Field */}
							<div>
								<div className="flex justify-between items-center mb-2">
									<label
										className={`text-sm font-medium ${colors.classes.textSecondary}`}
									>
										{t('Message Content')}
									</label>
									<span
										className={
											characterCount > maxCharsPerSegment
												? colors.classes.textWarning
												: colors.classes.textSecondary
										}
									>
										{characterCount} {t('characters')} ({segmentCount}{' '}
										{t(segmentCount === 1 ? 'segment' : 'segments')})
									</span>
								</div>
								<PlainTextTemplateEditor
									value={config.body ?? ''}
									onChange={(value) => {
										setConfig({ ...config, body: value });
									}}
									placeholder={t('Enter your SMS message...')}
									minHeight="120px"
									rows={4}
									contextAwareVariables={contextAwareVariables}
								/>
							</div>

							{/* Body Variable Mapping Modal */}
							{showBodyVariableMapping && (
								<Modal
									isOpen={showBodyVariableMapping}
									onClose={() => setShowBodyVariableMapping(false)}
									title={t('Manage Body Variables')}
								>
									<VariableMapping
										variables={getBodyVariablesForMapping()}
										mapping={config.bodyVariableMapping ?? {}}
										onSave={handleSaveBodyVariableMapping}
										onCancel={() => setShowBodyVariableMapping(false)}
										useDynamicFieldValue={true}
										availableData={getAvailableDataSources()}
										displayTransform={getDisplayText}
										enableAutoMap={false}
										allowEditVariableNames={true}
										description={t(
											'Define custom variables for your SMS body. Each variable name you create can be used in the body as {{variableName}}.',
										)}
									/>
								</Modal>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
